import React, { useState } from 'react';
import { Download, Eye, File, FileText, Image as ImageIcon, X, Loader2 } from 'lucide-react';
import { FeedbackAttachment } from '../types/feedback';
import { formatDateTimeInIST } from '../utils/mappers';

interface AttachmentListProps {
  attachments: FeedbackAttachment[];
  feedbackRequestId: number;
  onDelete?: (attachmentId: number) => Promise<void>;
  canDelete?: boolean;
}

const AttachmentList: React.FC<AttachmentListProps> = ({
  attachments,
  feedbackRequestId,
  onDelete,
  canDelete = false
}) => {
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [viewingFile, setViewingFile] = useState<{ url: string; name: string; type: string } | null>(null);

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const formatDate = (dateString: string): string => {
    return `${formatDateTimeInIST(dateString)} IST`;
  };

  const getFileIcon = (fileType: string, extension: string) => {
    if (fileType.startsWith('image/')) {
      return <ImageIcon className="text-blue-400" size={24} />;
    } else if (fileType === 'application/pdf' || extension === '.pdf') {
      return <FileText className="text-red-400" size={24} />;
    } else {
      return <File className="text-gray-400" size={24} />;
    }
  };

  const isViewableInBrowser = (fileType: string): boolean => {
    return (
      fileType.startsWith('image/') ||
      fileType === 'application/pdf'
    );
  };

  const handleDownload = async (attachmentId: number, fileName: string) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/Attachments/download/${attachmentId}`
      );
      
      if (!response.ok) {
        throw new Error('Failed to download file');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Error downloading file:', error);
      alert('Failed to download file');
    }
  };

  const handleView = async (attachment: FeedbackAttachment) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/Attachments/download/${attachment.id}`
      );
      
      if (!response.ok) {
        throw new Error('Failed to load file');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      setViewingFile({ url, name: attachment.fileName, type: attachment.fileType });
    } catch (error) {
      console.error('Error viewing file:', error);
      alert('Failed to view file');
    }
  };

  const handleDelete = async (attachmentId: number) => {
    if (!onDelete) return;
    
    if (!window.confirm('Are you sure you want to delete this attachment?')) {
      return;
    }

    setDeletingId(attachmentId);
    try {
      await onDelete(attachmentId);
    } finally {
      setDeletingId(null);
    }
  };

  const closeViewer = () => {
    if (viewingFile) {
      window.URL.revokeObjectURL(viewingFile.url);
      setViewingFile(null);
    }
  };

  if (attachments.length === 0) {
    return (
      <div className="text-center py-8 text-slate-400">
        <File className="mx-auto mb-2" size={48} />
        <p>No attachments</p>
      </div>
    );
  }

  return (
    <>
      <div className="space-y-3">
        {attachments.map((attachment) => (
          <div
            key={attachment.id}
            className="flex items-center justify-between p-4 bg-white rounded-xl border border-slate-200 hover:border-slate-300 transition-colors"
          >
            <div className="flex items-center gap-4 flex-1 min-w-0">
              {getFileIcon(attachment.fileType, attachment.fileExtension)}
              
              <div className="flex-1 min-w-0">
                <p className="text-slate-800 font-medium truncate">{attachment.fileName}</p>
                <div className="flex items-center gap-3 text-xs text-slate-400 mt-1">
                  <span>{formatFileSize(attachment.fileSize)}</span>
                  <span>•</span>
                  <span>{formatDate(attachment.uploadedAt)}</span>
                  <span>•</span>
                  <span className="truncate">Uploaded by {attachment.uploadedBy}</span>
                </div>
              </div>
            </div>

            <div className="flex items-center gap-2 ml-4">
              {isViewableInBrowser(attachment.fileType) && (
                <button
                  onClick={() => handleView(attachment)}
                  className="p-2 hover:bg-blue-50 rounded-lg transition-colors"
                  title="View"
                >
                  <Eye className="text-blue-500" size={20} />
                </button>
              )}
              
              <button
                onClick={() => handleDownload(attachment.id, attachment.fileName)}
                className="p-2 hover:bg-green-50 rounded-lg transition-colors"
                title="Download"
              >
                <Download className="text-green-500" size={20} />
              </button>

              {canDelete && (
                <button
                  onClick={() => handleDelete(attachment.id)}
                  disabled={deletingId === attachment.id}
                  className="p-2 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50"
                  title="Delete"
                >
                  {deletingId === attachment.id ? (
                    <Loader2 className="text-red-400 animate-spin" size={20} />
                  ) : (
                    <X className="text-red-400" size={20} />
                  )}
                </button>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* File Viewer Modal */}
      {viewingFile && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 animate-fade-in">
          <div className="bg-slate-900 rounded-2xl max-w-6xl w-full max-h-[90vh] overflow-hidden border border-slate-700">
            <div className="flex items-center justify-between p-4 border-b border-slate-700">
              <h3 className="text-white font-bold truncate flex-1">{viewingFile.name}</h3>
              <button
                onClick={closeViewer}
                className="p-2 hover:bg-slate-800 rounded-lg transition-colors ml-4"
              >
                <X className="text-white" size={24} />
              </button>
            </div>
            <div className="p-4 overflow-auto max-h-[calc(90vh-80px)]">
              {viewingFile.type.startsWith('image/') ? (
                <img
                  src={viewingFile.url}
                  alt={viewingFile.name}
                  className="max-w-full h-auto mx-auto"
                />
              ) : viewingFile.type === 'application/pdf' ? (
                <iframe
                  src={viewingFile.url}
                  className="w-full h-[calc(90vh-120px)]"
                  title={viewingFile.name}
                />
              ) : (
                <div className="text-center py-8 text-slate-400">
                  <File className="mx-auto mb-2" size={48} />
                  <p>Preview not available for this file type</p>
                  <button
                    onClick={() => {
                      const a = document.createElement('a');
                      a.href = viewingFile.url;
                      a.download = viewingFile.name;
                      a.click();
                    }}
                    className="mt-4 px-4 py-2 bg-gradient-to-r from-pink-500 to-purple-600 hover:from-pink-600 hover:to-purple-700 text-white rounded-lg transition-colors"
                  >
                    Download File
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AttachmentList;
